import { useRef, useState } from "react";

import {
  api,
  uploadBytes,
  uploadMultipart,
  type MediaRecord,
  type MediaTarget,
  type Tournament,
} from "../api";
import { canProbablyPlay, inspectVideo } from "../poster";
import { useAction, useLoad } from "../useLoad";

interface Props {
  teamId: string;
  tournament: Tournament;
  /** The heat this sheet is for, or `"tournament"` for requirement 8.2's track build and trophy shot. */
  target: MediaTarget | "tournament";
  names: Map<string, string>;
  onClose: () => void;
  onChanged: () => void;
  onError: (message: string) => void;
}

const ACCEPT = [
  "video/quicktime",
  "video/mp4",
  "video/x-m4v",
  "video/webm",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
].join(",");

/**
 * The media attached to one heat, or to the tournament. Requirements 8.1, 8.2, 8.6, 8.7.
 *
 * Requirement 8.1 is the one that gives this screen its point: footage attaches to a *specific heat*, so
 * selecting a heat plays the footage of that actual race. That is what turns the bracket from a
 * scoreboard into a record of the day, and it is also what makes the champion reel (8.4) assemblable
 * without anyone curating anything.
 */
export function MediaSheet({
  teamId,
  tournament,
  target,
  names,
  onClose,
  onChanged,
  onError,
}: Props) {
  const { data, loading, reload } = useLoad(
    () => api.media(teamId, tournament.tournamentId, target),
    [teamId, tournament.tournamentId, JSON.stringify(target)],
    onError,
  );
  const { busy, run } = useAction(onError);
  const [progress, setProgress] = useState<number | null>(null);
  const [stage, setStage] = useState<string>("");
  const [confirming, setConfirming] = useState<string | null>(null);
  const [moving, setMoving] = useState<MediaRecord | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const heatTarget: MediaTarget = target === "tournament" ? {} : target;
  const records = data?.media ?? [];

  const title =
    target === "tournament"
      ? "Tournament footage"
      : `Round ${(target as MediaTarget).round} · race ${((target as MediaTarget).index ?? 0) + 1}`;

  const upload = async (file: File) => {
    setProgress(0);
    setStage("Reading the clip");
    try {
      // Requirement 8.11 — poster frame first, client-side, before a single byte goes anywhere. Doing
      // it up front means a failed decode is known before the upload rather than after it.
      const details = file.type.startsWith("video/")
        ? await inspectVideo(file)
        : { durationSeconds: null, width: null, height: null, poster: null };

      setStage("Uploading");
      const started = await api.startMedia(teamId, tournament.tournamentId, {
        contentType: file.type,
        size: file.size,
        ...heatTarget,
      });

      let parts;
      try {
        parts = await uploadMultipart(started, file, setProgress);
      } catch (cause) {
        // Tell the server to drop the parts. Best effort: the bucket also aborts abandoned uploads
        // after seven days, so this is politeness rather than correctness.
        void api.abandonMedia(teamId, tournament.tournamentId, started.key, started.uploadId).catch(() => {});
        throw cause;
      }

      if (details.poster) {
        setStage("Saving the thumbnail");
        // Failing here is survivable — the record just has no poster, which is a state every list
        // already renders. Losing the video because its thumbnail did not upload would not be.
        await uploadBytes(started.poster, details.poster).catch(() => {});
      }

      setStage("Finishing");
      await api.finishMedia(teamId, tournament.tournamentId, {
        mediaId: started.mediaId,
        key: started.key,
        uploadId: started.uploadId,
        contentType: file.type,
        parts,
        durationSeconds: details.durationSeconds ?? undefined,
        width: details.width ?? undefined,
        height: details.height ?? undefined,
        ...heatTarget,
      });
      reload();
      onChanged();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "That upload didn't finish.");
    } finally {
      setProgress(null);
      setStage("");
    }
  };

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <div className="sheet-inner">
        <div className="row">
          <p className="section-title grow" style={{ margin: 0 }}>
            {title}
          </p>
          <button className="btn small" onClick={onClose} disabled={progress !== null}>
            Close
          </button>
        </div>

        {target !== "tournament" && (
          <p className="muted" style={{ margin: 0 }}>
            {((target as MediaTarget).round !== undefined
              ? tournament.heats.find(
                  (heat) =>
                    heat.round === (target as MediaTarget).round &&
                    heat.index === (target as MediaTarget).index,
                )?.participants ?? []
              : []
            )
              .map((id) => names.get(id) ?? "—")
              .join(" v ")}
          </p>
        )}

        {progress !== null ? (
          <div className="centre" style={{ padding: "28px 8px" }}>
            <div className="spinner" />
            <p className="muted">
              {stage}
              {progress > 0 && progress < 1 ? ` ${Math.round(progress * 100)}%` : "…"}
            </p>
          </div>
        ) : (
          <button className="btn primary wide" onClick={() => input.current?.click()}>
            Add a video or photo
          </button>
        )}

        {records.length === 0 && !loading && progress === null && (
          <p className="muted" style={{ textAlign: "center" }}>
            {target === "tournament"
              ? "The track build, the crowd, the trophy shot."
              : "Nothing filmed for this race yet."}
          </p>
        )}

        <div className="clips">
          {records.map((record) => (
            <Clip
              key={record.mediaId}
              record={record}
              busy={busy}
              onMove={() => setMoving(record)}
              onDelete={() => setConfirming(record.mediaId)}
            />
          ))}
        </div>

        {/*
          Requirement 4.3 — a confirmation defaults to Cancel. Cancel is first in the DOM and carries the
          plain styling; the destructive choice is the one that has to be reached for. This is the only
          control in the app that removes content, which is why 8.14 makes it explicit-only and 8.15
          keeps it recoverable for a month.
        */}
        {confirming && (
          <div className="banner error">
            <p style={{ margin: "0 0 10px" }}>Delete this for good?</p>
            <div className="inline-form">
              <button className="btn grow" style={{ flex: 1 }} onClick={() => setConfirming(null)}>
                Keep it
              </button>
              <button
                className="btn danger"
                disabled={busy}
                onClick={() =>
                  void run(
                    () => api.deleteMedia(teamId, tournament.tournamentId, confirming),
                    () => {
                      setConfirming(null);
                      reload();
                      onChanged();
                    },
                  )
                }
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {moving && (
          <MovePicker
            tournament={tournament}
            names={names}
            record={moving}
            busy={busy}
            onCancel={() => setMoving(null)}
            onPick={(to) =>
              void run(
                () => api.moveMedia(teamId, tournament.tournamentId, moving.mediaId, to),
                () => {
                  setMoving(null);
                  reload();
                  onChanged();
                },
              )
            }
          />
        )}

        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void upload(file);
          }}
        />
      </div>
    </div>
  );
}

/**
 * One clip. Requirement 8.10's graceful degradation lives here.
 *
 * The accepted v1 limitation is that an iPhone `.mov` will not play outside Safari: Chrome's HEVC
 * support is hardware-dependent and the container is poorly supported generally. Requirement 8.10's
 * instruction is *never a broken player* — so rather than mounting a `<video>` and hoping, this asks the
 * browser first with `canPlayType` and shows the poster frame with a download instead when the answer is
 * no. A parent on an Android phone sees a still of the race and a way to save it, which is a worse
 * experience than Safari's and a much better one than a black rectangle with a slash through it.
 *
 * Degrading the *upload* to suit browsers would be the wrong trade — requirement 8.12 works out that a
 * whole tournament's footage is well under a gigabyte, so there is nothing to gain by lowering the
 * quality the Apple TV gets.
 */
function Clip({
  record,
  busy,
  onMove,
  onDelete,
}: {
  record: MediaRecord;
  busy: boolean;
  onMove: () => void;
  onDelete: () => void;
}) {
  const [playing, setPlaying] = useState(false);
  // Requirement 8.9 — pick from the variants rather than assuming one. In v1 there is only the
  // original; the day a transcoded copy appears this prefers whichever the browser will actually play.
  const variant =
    record.playbackVariants.find((entry) => canProbablyPlay(entry.contentType)) ?? null;
  const source = variant ?? record.playbackVariants[0] ?? null;
  const playable = variant !== null && Boolean(variant.url);

  return (
    <div className="clip">
      {playing && playable ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- home video of a toy race
        <video src={variant?.url ?? undefined} poster={record.posterUrl ?? undefined} controls autoPlay playsInline />
      ) : record.posterUrl ? (
        <button className="clip-poster" onClick={() => playable && setPlaying(true)} disabled={!playable}>
          <img src={record.posterUrl} alt="" loading="lazy" />
          {playable && <span className="clip-play" aria-hidden="true">▶</span>}
        </button>
      ) : (
        <button
          className="clip-poster empty"
          onClick={() => playable && setPlaying(true)}
          disabled={!playable}
        >
          <span aria-hidden="true">{record.kind === "video" ? "🎬" : "🖼"}</span>
        </button>
      )}

      <div className="clip-meta">
        <span className="muted">
          {record.kind === "video"
            ? `${record.durationSeconds ? `${record.durationSeconds.toFixed(1)}s · ` : ""}${megabytes(record.bytes)}`
            : megabytes(record.bytes)}
          {record.movedFrom && ` · moved from race ${prettyHeat(record.movedFrom)}`}
        </span>
        {!playable && source?.url && (
          <span className="note">
            This browser can't play it. Open in Safari, or{" "}
            <a href={source.url} download>
              download
            </a>
            .
          </span>
        )}
        <div className="row subrow">
          <span className="grow" />
          <button className="btn small" disabled={busy} onClick={onMove}>
            Move
          </button>
          <button className="btn small danger" disabled={busy} onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/** Requirement 8.7 — reassign between heats, including to and from tournament level. */
function MovePicker({
  tournament,
  names,
  record,
  busy,
  onCancel,
  onPick,
}: {
  tournament: Tournament;
  names: Map<string, string>;
  record: MediaRecord;
  busy: boolean;
  onCancel: () => void;
  onPick: (target: MediaTarget) => void;
}) {
  return (
    <div className="card">
      <div className="row">
        <p className="section-title grow" style={{ margin: 0 }}>
          Move to
        </p>
        <button className="btn small" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <button
        className="lane"
        disabled={busy || record.round === null}
        onClick={() => onPick({})}
      >
        <span className="grow name">The tournament itself</span>
      </button>
      {tournament.heats
        .filter((heat) => heat.participants.length > 0)
        .map((heat) => (
          <button
            key={`${heat.round}-${heat.index}`}
            className="lane"
            disabled={busy || (heat.round === record.round && heat.index === record.index)}
            onClick={() => onPick({ round: heat.round, index: heat.index })}
          >
            <span className="place">{heat.round}</span>
            <span className="grow name">
              {heat.participants.map((id) => names.get(id) ?? "—").join(" v ")}
            </span>
          </button>
        ))}
    </div>
  );
}

function megabytes(bytes: number | null): string {
  if (!bytes) return "—";
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** `03#001` reads as "round 3, race 2" to nobody. */
function prettyHeat(key: string): string {
  const [round, index] = key.split("#");
  if (index === undefined) return key;
  return `${Number(round)}·${Number(index) + 1}`;
}
